import { ArrowRight } from "@phosphor-icons/react";
import { C } from "@/brand/tokens";
import { PHOTOS } from "@/constants/photos";
import { disciplines, fitSignals, livedComplexity } from "@/brand/content";
import { ChamberChrome } from "@/components/common/ChamberChrome";

/**
 * TrustAnchorsSection — Smoked Ink chamber (IV. THE JUDGMENT BEHIND THE WORK).
 *
 * Authoring moves (mirrors hero's right-bleed with a LEFT-bleed):
 *   - Chamber chrome rail at top
 *   - Contemplative portrait BLEEDS to the viewport LEFT edge with the
 *     same seam architecture (6px Slate Olive doorframe + 1px brass
 *     hairline + inset shadow) — creates structural symmetry of
 *     asymmetry across the page
 *   - Founder credential plate locked into the portrait's RIGHT edge
 *     (mirror of hero quote card)
 *   - Credentials rail in the right text field with brass-numbered
 *     architectural lists
 */
export function TrustAnchorsSection() {
  const IMG_W = "38%";

  return (
    <section
      id="about"
      data-testid="trust-anchors"
      style={{
        background: C.bgDark,
        color: C.textOnDark,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <ChamberChrome
        numeral="IV."
        title="The Judgment Behind The Work"
        category="About"
        meta="Founder & Principal"
        tone="dark"
      />

      <div className="relative">
        {/* Seam architecture (mirrored — left bleed): brass hairline + slate */}
        <span
          aria-hidden="true"
          className="hidden lg:block absolute top-0 bottom-0"
          style={{ left: IMG_W, width: 1, background: C.accentMetal }}
        />
        <span
          aria-hidden="true"
          className="hidden lg:block absolute top-0 bottom-0"
          style={{
            left: `calc(${IMG_W} + 1px)`,
            width: 6,
            background: C.supportOrganic,
          }}
        />

        {/* Portrait — bleeds to viewport LEFT edge */}
        <figure
          className="hidden lg:block absolute top-0 left-0 bottom-0"
          style={{
            width: IMG_W,
            boxShadow: "inset -40px 0 60px -32px rgba(22,24,27,0.78)",
          }}
        >
          <div
            className="absolute inset-0"
            style={{ background: C.supportOrganic }}
          />
          <img
            src={PHOTOS.contemplative}
            alt="Katrina Bernard, Founder & Principal of Faith 1st"
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              objectPosition: "center 18%",
              filter: "saturate(0.74) contrast(1.05) brightness(0.94)",
            }}
            loading="lazy"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `linear-gradient(180deg, rgba(22,24,27,0) 50%, rgba(22,24,27,0.55) 100%)`,
            }}
          />

          {/* Rotated brass marker on far-LEFT viewport edge */}
          <div
            className="absolute left-0 top-0 bottom-0 flex items-center"
            style={{ width: 36 }}
          >
            <div
              className="flex items-center justify-center"
              style={{
                writingMode: "vertical-rl",
                height: "100%",
                width: "100%",
                background: C.accentMetal,
                color: C.bgDark,
                padding: "32px 0",
              }}
            >
              <span
                className="font-inter text-[10px] uppercase font-bold tabular-nums"
                style={{ letterSpacing: "0.42em", color: C.bgDark }}
              >
                Founder &amp; Principal
                <span style={{ margin: "0 14px" }}>·</span>
                Katrina Bernard, JD&nbsp;|&nbsp;CPC
              </span>
            </div>
          </div>

          {/* Credential info lives in the brass vertical marker on the
              far-left viewport edge and in the typography stack to the
              right — no overlay plate needed here. */}
        </figure>

        {/* Text content — right of the bleed */}
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-14 py-24 lg:py-32 relative">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="hidden lg:block lg:col-span-4" />
            <div className="lg:col-span-8 lg:pl-8">
              <div className="flex items-center gap-3 mb-7">
                <span
                  aria-hidden="true"
                  className="inline-block h-px w-10"
                  style={{ background: C.accentMetal }}
                />
                <p
                  className="font-inter text-[11px] uppercase font-semibold tabular-nums"
                  style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
                >
                  § 04
                </p>
              </div>
              <h2
                className="font-cormorant"
                style={{
                  color: C.textOnDark,
                  fontSize: "clamp(2rem, 3.6vw, 3rem)",
                  fontWeight: 400,
                  lineHeight: 1.04,
                  letterSpacing: "-0.01em",
                }}
              >
                Warm in conversation.
                <br />
                Composed when
                <br />
                the stakes get high.
              </h2>
              <p
                className="mt-10 font-inter leading-[1.7] max-w-[54ch]"
                style={{ color: C.whiteOnDark, fontSize: "1.05rem" }}
              >
                Faith&nbsp;1st reflects two decades of practice at the
                intersection of employment law, senior HR judgment, certified
                executive coaching, and sensitive facilitation. The work is
                steady, candid, and held with discretion — designed for
                leaders carrying decisions that matter.
              </p>

              {/* Disciplines — continuous typographic mass */}
              <div className="mt-14">
                <p
                  className="font-inter text-[10px] uppercase font-semibold mb-5"
                  style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
                >
                  Cross-disciplinary judgment
                </p>
                <p
                  data-testid="disciplines-mass"
                  className="font-cormorant"
                  style={{
                    color: C.textOnDark,
                    fontSize: "clamp(1.3rem, 1.8vw, 1.7rem)",
                    fontWeight: 400,
                    lineHeight: 1.35,
                    letterSpacing: "-0.005em",
                  }}
                >
                  {disciplines.map((d, i) => (
                    <span key={d}>
                      {d}
                      {i < disciplines.length - 1 && (
                        <span
                          aria-hidden="true"
                          style={{
                            color: C.accentMetal,
                            margin: "0 0.55em",
                            fontStyle: "italic",
                          }}
                        >
                          ·
                        </span>
                      )}
                    </span>
                  ))}
                </p>
              </div>

              <div className="mt-10">
                <p
                  className="font-inter text-[10px] uppercase font-semibold mb-5"
                  style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
                >
                  Sectors served
                </p>
                <p
                  data-testid="sectors-mass"
                  className="font-cormorant italic"
                  style={{
                    color: C.whiteOnDark,
                    fontSize: "clamp(1.05rem, 1.3vw, 1.25rem)",
                    fontWeight: 400,
                    lineHeight: 1.45,
                  }}
                >
                  {fitSignals.map((s, i) => (
                    <span key={s}>
                      {s}
                      {i < fitSignals.length - 1 && (
                        <span
                          aria-hidden="true"
                          style={{
                            color: C.accentMetal,
                            margin: "0 0.5em",
                          }}
                        >
                          /
                        </span>
                      )}
                    </span>
                  ))}
                </p>
              </div>

              <div className="mt-14">
                <p
                  className="font-inter text-[10px] uppercase font-semibold mb-5"
                  style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
                >
                  Lived organizational complexity
                </p>
                <ul>
                  {livedComplexity.map((c, i) => (
                    <li
                      key={c}
                      data-testid={`complexity-${i}`}
                      className="py-4 flex items-baseline gap-5"
                      style={{ borderBottom: `1px solid ${C.brassSoft}` }}
                    >
                      <span
                        className="font-inter text-[10px] uppercase font-bold tabular-nums shrink-0"
                        style={{
                          color: C.accentMetal,
                          letterSpacing: "0.26em",
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span
                        className="font-inter leading-[1.55]"
                        style={{ color: C.textOnDark, fontSize: "0.98rem" }}
                      >
                        {c}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <a
                href="#"
                data-testid="founder-bio-link"
                className="group mt-12 inline-flex items-center gap-2 font-inter text-[11px] uppercase font-semibold pb-2 border-b transition-all"
                style={{
                  color: C.accentMetal,
                  borderColor: C.accentMetal,
                  letterSpacing: "0.26em",
                }}
              >
                Read the full biography
                <ArrowRight
                  size={13}
                  weight="bold"
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
            </div>
          </div>

          {/* Mobile-only portrait */}
          <div className="lg:hidden mt-14">
            <figure className="relative">
              <div
                className="relative overflow-hidden"
                style={{
                  aspectRatio: "4 / 5",
                  background: C.supportOrganic,
                  border: `1px solid ${C.brassSoft}`,
                }}
              >
                <img
                  src={PHOTOS.contemplative}
                  alt="Katrina Bernard, Founder & Principal of Faith 1st"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{
                    objectPosition: "center 18%",
                    filter: "saturate(0.74) contrast(1.05)",
                  }}
                  loading="lazy"
                />
              </div>
              <figcaption
                className="mt-5 p-5"
                style={{
                  background: C.bgDark,
                  border: `1px solid ${C.brassLine}`,
                }}
              >
                <p
                  className="font-cormorant"
                  style={{ color: C.textOnDark, fontSize: "1rem" }}
                >
                  Katrina Bernard, JD&nbsp;|&nbsp;CPC
                </p>
                <p
                  className="mt-2 font-inter text-[9.5px] uppercase font-semibold"
                  style={{ color: C.accentMetal, letterSpacing: "0.30em" }}
                >
                  Founder &amp; Principal
                </p>
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}
